import {
  Grid,
  CardHeader,
  CardContent,
  Box,
  TextField,
  Avatar,
  Button,
  IconButton,
  Typography,
  FormHelperText,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import React, { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Form, FormikProvider, useFormik } from "formik";
import { IMAGE_BASE } from "../../utils/constants";
import LoadingButton from "@mui/lab/LoadingButton";
import CardLayout from "../../layouts/CardLayout";
import { Helmet } from "react-helmet-async";
import { toast } from "react-toastify";
import { RemoveCircle } from "@mui/icons-material";
import errorHandler from "../../utils/errorHandler";
import { CategorySchema } from "../../utils/Schema";
import { apiCategory } from "../../services/AllServices";
import { trimFormValues } from "../../utils/helpers";

// Styled components for image preview container and remove button
const PreviewContainer = styled(Box)(({ theme }) => ({
  position: "relative",
  width: 100,
  height: 100,
  borderRadius: theme.shape.borderRadius,
  overflow: "hidden",
  boxShadow: theme.shadows[3],
  cursor: "pointer",
  "&:hover .remove-btn": {
    opacity: 1,
  },
}));

const RemoveButton = styled(IconButton)(({ theme }) => ({
  position: "absolute",
  top: 4,
  right: 4,
  backgroundColor: theme.palette.error.main,
  color: theme.palette.common.white,
  padding: 4,
  opacity: 0.8,
  transition: "opacity 0.3s",
  "&:hover": {
    backgroundColor: theme.palette.error.dark,
    opacity: 1,
  },
}));

function AddEditCategory() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState();

  // Separate state for existing uploaded images (filenames)
  const [existingImages, setExistingImages] = useState([]);
  // New selected images as files
  const [selectedImages, setSelectedImages] = useState([]);
  // Previews array mixing both existing URLs and new local previews
  const [imagePreviews, setImagePreviews] = useState([]);

  const fileInputRef = useRef(null);
  const { id } = useParams();

  const fetchTaskData = useCallback(async () => {
    if (id) {
      setLoading(true);
      try {
        const response = await apiCategory.getById({ id });
        const resData = response?.data?.data || {};
        const existingImgs = resData?.categoryImage || [];

        setExistingImages(existingImgs);
        setSelectedImages([]);
        setImagePreviews(existingImgs.map((img) => IMAGE_BASE + img));
        setData(resData);
      } catch (error) {
        errorHandler(error);
      } finally {
        setLoading(false);
      }
    }
  }, [id]);

  useEffect(() => {
    fetchTaskData();
  }, [fetchTaskData]);

  const formik = useFormik({
    enableReinitialize: true,
    validationSchema: CategorySchema,
    initialValues: {
      name: data?.name || "",
      description: data?.description || "",
      images: [],
      status: data?.status || 1,
    },
    onSubmit: async (values) => {
      setLoading(true);
      try {
        // Trim all string values in the form values
        const trimmedValues = trimFormValues(values);
        
        const formData = new FormData();
        if (id) formData.append("id", id);
        formData.append("name", trimmedValues.name);
        formData.append("description", trimmedValues.description);
        formData.append("status", trimmedValues.status);
    
        // Append existing images as strings under the same key
        existingImages.forEach((img) => {
          formData.append("categoryImage", img);  // sending filename or URL as string
        });
    
        // Append newly selected image files
        selectedImages.forEach((file) => {
          formData.append("categoryImage", file);
        });
    
        const response = id
          ? await apiCategory.edit(formData)
          : await apiCategory.add(formData);
    
        toast.success(response?.data?.message, { toastId: "success" });
        navigate(-1);
      } catch (error) {
        errorHandler(error);
      } finally {
        setLoading(false);
      }
    },
    
  });

  const handleImageChange = (event) => {
    const files = Array.from(event.currentTarget.files);
    // Allowed file types
    const allowedTypes = ['image/png', 'image/jpg', 'image/jpeg', 'image/webp'];
    const allowedExtensions = ['.png', '.jpg', '.jpeg', '.webp'];

    // Validate file types
    const invalidFiles = files.filter(file => {
      const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
      return !allowedTypes.includes(file.type) && !allowedExtensions.includes(fileExtension);
    });

    if (invalidFiles.length > 0) {
      toast.error(`Invalid file format. Only PNG, JPG, JPEG, and WEBP formats are allowed.`);
      event.target.value = ''; // Clear the input
      return;
    }

    const filtered = files.filter(
      (file) => {
        const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
        return (allowedTypes.includes(file.type) || allowedExtensions.includes(fileExtension)) &&
          !selectedImages.some((img) => img.name === file.name);
      }
    );

    if (filtered.length === 0) return;

    const newSelectedImages = [...selectedImages, ...filtered];

    setSelectedImages(newSelectedImages);
    setImagePreviews([
      ...existingImages.map((img) => IMAGE_BASE + img),
      ...newSelectedImages.map((file) => URL.createObjectURL(file)),
    ]);
    formik.setFieldValue("images", newSelectedImages);

    event.target.value = null;
  };

  const handleRemoveImage = (index) => {
    if (index < existingImages.length) {
      // Remove from existingImages
      const updatedExisting = [...existingImages];
      updatedExisting.splice(index, 1);
      setExistingImages(updatedExisting);
      // Update previews after removal
      setImagePreviews([
        ...updatedExisting.map((img) => IMAGE_BASE + img),
        ...selectedImages.map((file) => URL.createObjectURL(file)),
      ]);
    } else {
      // Remove from selectedImages
      const selectedIndex = index - existingImages.length;
      const updatedSelected = [...selectedImages];
      updatedSelected.splice(selectedIndex, 1);
      setSelectedImages(updatedSelected);
      setImagePreviews([
        ...existingImages.map((img) => IMAGE_BASE + img),
        ...updatedSelected.map((file) => URL.createObjectURL(file)),
      ]);
      formik.setFieldValue("images", updatedSelected);
    }
  };

  return (
    <Box>
      <Helmet>
        <title>{id ? "Edit" : "Add"} Category</title>
      </Helmet>
      <CardLayout>
        <CardHeader title={`${id ? "Edit" : "Add"} Category`} />
        <CardContent>
          <FormikProvider value={formik}>
            <Form autoComplete="off" noValidate onSubmit={formik.handleSubmit}>
              <Grid container spacing={3}>

                {/* Image Upload Section */}
                <Grid size={{ xs: 12 }} md={12}>
                  <input
                    accept=".png,.jpg,.jpeg,.webp,image/png,image/jpeg,image/jpg,image/webp"
                    type="file"
                    ref={fileInputRef}
                    onChange={handleImageChange}
                    style={{ display: "none" }}
                    multiple
                  />
                  <Box
                    mb={1}
                    display="flex"
                    flexWrap="wrap"
                    gap={2}
                    alignItems="center"
                  >
                    {imagePreviews.length === 0 && (
                      <Typography variant="subtitle2" color="text.secondary" sx={{ width: "100%" }}>
                        No images uploaded yet
                      </Typography>
                    )}
                    {imagePreviews.map((src, index) => (
                      <PreviewContainer
                        key={index}
                        title="Click to change image"
                        onClick={() => fileInputRef.current.click()}
                      >
                        <Avatar
                          src={src}
                          alt={`preview-${index}`}
                          variant="rounded"
                          sx={{ width: "100%", height: "100%" }}
                        />
                        <RemoveButton
                          size="small"
                          aria-label="Remove image"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveImage(index);
                          }}
                          className="remove-btn"
                        >
                          <RemoveCircle fontSize="small" />
                        </RemoveButton>
                      </PreviewContainer>
                    ))}
                  </Box>
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={() => fileInputRef.current.click()}
                    sx={{ textTransform: "none" }}
                  >
                    {id ? "Edit Images" : "Upload Images"}
                  </Button>
                  {formik.touched.images && formik.errors.images && (
                    <FormHelperText error>{formik.errors.images}</FormHelperText>
                  )}
                </Grid>

                {/* Category Name */}
                <Grid size={{ xs: 12 }} md={12}>
                  <TextField
                    fullWidth
                    label="Main Category Name"
                    value={formik.values.name}
                    onChange={formik.handleChange}
                    required
                    name="name"
                    error={Boolean(formik.touched.name && formik.errors.name)}
                    helperText={formik.touched.name && formik.errors.name}
                    inputProps={{ maxLength: 50 }}
                    placeholder="e.g., SUVs, Sedans, Trucks, Motorcycles"
                  />
                </Grid>

                {/* Description */}
                <Grid size={{ xs: 12 }} md={12}>
                  <TextField
                    fullWidth
                    label="Description"
                    multiline
                    rows={3}
                    
                    value={formik.values.description}
                    onChange={formik.handleChange}
                    name="description"
                    error={Boolean(formik.touched.description && formik.errors.description)}
                    helperText={formik.touched.description && formik.errors.description}
                    inputProps={{ maxLength: 200 }}
                    placeholder="Brief description of the category for SEO and clarity"
                  />
                </Grid>

                {/* Submit Button */}
                <Grid size={{ xs: 12 }}>
                  <Box mt={3} width="100%">
                    <LoadingButton
                      fullWidth
                      size="large"
                      type="submit"
                      variant="contained"
                      loading={loading}
                    >
                      {id ? "Update" : "Add"} Category
                    </LoadingButton>
                  </Box>
                </Grid>

              </Grid>
            </Form>
          </FormikProvider>
        </CardContent>
      </CardLayout>
    </Box>
  );
}

export default AddEditCategory;
